import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/material";
import {
  ConfigMetaData,
  ConfigMetaDataAccess,
} from "@kahunmedical/common-configuration";
import { KahunSliderTabs } from "../presentaional/kahunSliderTabs";
import { KahunSettingEditor } from "./kahunSettingEditor";
import { UseConfigurationSettingsManager } from "../common/useConfigurationSettingsManager";

import { SettingsButton } from "../presentaional/controls/settingsButton";
import {
  SaveSettingsDialog,
  SaveStatus,
  SaveStatusObject,
} from "../presentaional/saveSettingsDialog";
import clsx from "clsx";
import { useHistory, useRouteMatch } from "react-router";
import { ConfigurationSettingList } from "@kahunmedical/common-configuration/dist/lib/settings";
import { SAVE_DIALOG_AUTO_CLOSE_TIMEOUT } from "./saveDialogAutoCloseTimout";
import { ConfigurationDottedKey } from "@kahunmedical/common-configuration/dist/lib/annotations";
import { FilterSettingsSearchBox } from "../presentaional/controls/filterSettingsSearchBox";
import Fuse from "fuse.js";
import { DeleteForever, SaveOutlined } from "@mui/icons-material";
import { MASTER_CLINIC_DISABLED_ITEMS } from "fe-common/commonTypes/masterClinicDefaults";
import { MasterClinicWarning } from "../common/MasterClinicWarning";
import { LoadingProgress } from "../../loader/loadingProgress";
//region [[ Styles ]]

//region [[ Styles ]]
const KahunConfigurationManagementPageView = styled("div")({
  display: "flex",
  flexFlow: "column",
  width: "100%",
  height: "100%",
  minWidth: 770,
  direction: "ltr",
});

const ClearSettingsButton = styled(SettingsButton)({
  background: "#efefef",
  "& span": {
    color: "#232323",
  },
  "& svg": {
    height: "19px",
    margin: "0 2px",
  },
  "&:hover": {
    "& span": {
      color: "#5A52C3",
    },
    background: "#E6E4FF",
  },
});

const SaveButton = styled(SettingsButton)({});

const ClearChangesButtons = styled(SaveButton)({
  background: "#efefef",
  color: "#232323",
});

const HeaderControls = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "6px 0",
});

const SettingsContent = styled("div")({
  height: "calc(100% - 38px)",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface KahunConfigurationManagementPageProps {
  className?: string;
  /***
   * Whether display the backoffice section, as it's inner, high level
   * config which is being supported only in clinic management
   */
  showBackofficeSettings?: boolean;
  /***
   * Use merged config settings to tell what originated the value
   * see either UseConfigurationSettingsManager() hook or:
   * https://github.com/kahunmedical/kne.common-configuration/blob/master/src/lib/settings.ts
   */
  settingsManager: UseConfigurationSettingsManager;
  /** Use Promise so that the component can display notification
   *  on success or error and asked to retry **/
  configSaver: (newConfig: ConfigurationSettingList) => Promise<any>;
  /***
   * Used to show the user what will be deleted if "ResetDefault" will be clicked,
   * sometimes we will want the clinic data to be rested, and sometimes the localStorage
   * hence we want the parent to calculate this for us as we use current settings in the
   * MergedConfigurationSettings only for the settings pending to be saved,
   * and use the names settings as one of the defaults in the hierarchy
   * @see @kahunmedical/common-configuration/MergedConfigurationSettings
   * (https://github.com/kahunmedical/kne.common-configuration/blob/master/src/lib/settings.ts#L80)
   */
  getAllNonDefaultSettings?: () => ConfigurationSettingList;
  /** Use Promise so that the component can display notification
   *  on success or error and asked to retry **/
  resetDefaults: (root: string) => Promise<any>;
  disabledItems?: string[];
  isMasterClinic?: boolean;
}

enum SaveMode {
  SaveCurrentChanges,
  ResetDefaults,
  ClearCurrentChanges,
  ImportExport,
}

type AreYouSureDialogProps = {
  text: any;
  isOpen: boolean;
  diff: ConfigurationSettingList;
  isSaving: boolean;
  saveStatus: SaveStatusObject;
  saveMode: SaveMode;
  root?: ConfigurationDottedKey;
  saveText?: string;
};
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
export const KahunConfigurationManagementPage = ({
  settingsManager,
  showBackofficeSettings,
  ...props
}: KahunConfigurationManagementPageProps) => {
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState<string>(null);

  const mainEntries = useRef<ConfigMetaData[]>();

  const allMetaData = useRef<ConfigMetaData[]>();

  const configMetaDataAccess = useRef<ConfigMetaDataAccess>(
    new ConfigMetaDataAccess(),
  );

  const fuseRef = useRef<Fuse<ConfigMetaData>>(null);

  const [areYouSureDialog, setShowAreYouSureDialogProps] = useState<
    AreYouSureDialogProps
  >({
    diff: null,
    isOpen: false,
    text: null,
    isSaving: false,
    saveStatus: null,
    saveMode: SaveMode.ImportExport,
    root: null,
    saveText: null,
  });

  const [importExport, setImportExport] = useState<AreYouSureDialogProps>({
    diff: null,
    isOpen: false,
    text: null,
    isSaving: false,
    saveStatus: null,
    saveMode: SaveMode.ResetDefaults,
    root: null,
    saveText: null,
  });

  useEffect(() => {
    setLoading(true);
    configMetaDataAccess.current
      .getMetadataForKey()
      .then((md) => (mainEntries.current = md))
      .then(() => configMetaDataAccess.current.getAllMettaData())
      .then((allMd) => {
        allMetaData.current = allMd;
        fuseRef.current = new Fuse(allMd || [], {
          keys: ["description", "displayName"],
          threshold: 0.4,
        });
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false));
  }, [setLoading]);

  const closeAreYouSureDialog = () => {
    // First close
    setShowAreYouSureDialogProps((dialogProps) => ({
      ...dialogProps,
      isOpen: false,
    }));
    // Then reset - give the animation time to finish - otherwise it looks glitchy
    setTimeout(
      () =>
        setShowAreYouSureDialogProps((dialogProps) => ({
          ...dialogProps,
          saveStatus: null,
          diff: null,
          text: null,
          saveText: null,
        })),
      200,
    );
  };

  const closeImportExportDialog = () => {
    // First close
    setImportExport((dialogProps) => ({
      ...dialogProps,
      isOpen: false,
    }));
    // Then reset - give the animation time to finish - otherwise it looks glitchy
    setTimeout(
      () =>
        setImportExport((dialogProps) => ({
          ...dialogProps,
          saveStatus: null,
          diff: null,
          text: null,
          saveText: null,
        })),
      200,
    );
  };

  const setSaveStatus = (saveStatus) =>
    setShowAreYouSureDialogProps((dialogProps) => ({
      ...dialogProps,
      saveStatus,
    }));

  const setIsSaving = (isSaving) =>
    setShowAreYouSureDialogProps((dialogProps) => ({
      ...dialogProps,
      isSaving,
    }));

  const resetDefault = () => {
    setIsSaving(true);
    props
      .resetDefaults(areYouSureDialog?.root)
      .then(() => {
        settingsManager.setIsDirty(false);

        setSaveStatus({
          status: SaveStatus.Success,
          message: "Successfully cleared settings to defaults",
        });

        setTimeout(closeAreYouSureDialog, SAVE_DIALOG_AUTO_CLOSE_TIMEOUT);
      })
      .catch((e) =>
        setSaveStatus({ status: SaveStatus.Error, message: "Error: " + e }),
      )
      .finally(() => setIsSaving(false));
  };

  const handleSave = (editedValue: string) => {
    if (areYouSureDialog.saveMode === SaveMode.ResetDefaults) {
      return resetDefault();
    }
    setIsSaving(true);
    // If we were asked to clear current changes just save an empty object
    let valueToSave = settingsManager.settings?.getNonDefaultSettings?.();
    if (areYouSureDialog.saveMode === SaveMode.ClearCurrentChanges) {
      valueToSave = {};
    }
    if (areYouSureDialog.saveMode === SaveMode.ImportExport) {
      valueToSave = JSON.parse(editedValue);
    }
    props
      ?.configSaver?.(valueToSave)
      .then(() => {
        settingsManager.setIsDirty(false);

        setSaveStatus({
          status: SaveStatus.Success,
          message: "Saved Successfully",
        });

        setTimeout(closeAreYouSureDialog, SAVE_DIALOG_AUTO_CLOSE_TIMEOUT);
      })
      .catch((e) =>
        setSaveStatus({
          status: SaveStatus.Error,
          message: "Error:" + e.message,
        }),
      )
      .finally(() => setIsSaving(false));
  };

  const disableDialogAction =
    areYouSureDialog?.isSaving ||
    areYouSureDialog?.saveStatus?.status === SaveStatus.Success;

  const getAllNonDefaultForRoot = (root: ConfigurationDottedKey) => {
    const allNonDefault = props.getAllNonDefaultSettings?.();

    return Object.entries(allNonDefault).reduce((acc, [k, v]) => {
      if (k.startsWith(root)) return { ...acc, [k]: v };
      return acc;
    }, {});
  };

  const handleResetDefaultClick = (root: ConfigurationDottedKey) => {
    setShowAreYouSureDialogProps((dialogProps) => ({
      ...dialogProps,
      isOpen: true,
      text: "The flowing settings will be deleted, are you sure?",
      diff: getAllNonDefaultForRoot(root),
      saveMode: SaveMode.ResetDefaults,
      root,
      saveText: "Yes - Delete all these settings",
    }));
  };

  const handleImportExportClick = (root: ConfigurationDottedKey) => {
    setImportExport((dialogProps) => ({
      ...dialogProps,
      isOpen: true,
      text: "Copy/Past these settings",
      diff: getAllNonDefaultForRoot(root),
      saveMode: SaveMode.ImportExport,
      root,
      saveText: "Save",
    }));
  };

  const openSaveDialog = () => {
    // Since after saving we reload the settings the diff wil be deleted,
    // so we keep a copy everytime the save button is being clicked
    setShowAreYouSureDialogProps((dialog) => ({
      ...dialog,
      diff: settingsManager.settings?.getNonDefaultSettings?.(),
      text: props.isMasterClinic ? (
        <MasterClinicWarning />
      ) : (
        "Save the flowing settings?"
      ),
      isOpen: true,
      saveMode: SaveMode.SaveCurrentChanges,
    }));
  };

  const openResetLastChangesDialog = () => {
    // Since after saving we reload the settings the diff wil be deleted,
    // so we keep a copy everytime the save button is being clicked
    setShowAreYouSureDialogProps((dialog) => ({
      ...dialog,
      text: "Are you sure? The following changes will be removed",
      diff: settingsManager.settings?.getNonDefaultSettings?.(),
      isOpen: true,
      saveMode: SaveMode.ClearCurrentChanges,
      saveText: "Yes - clear",
    }));
  };

  const searchResults = useMemo(() => {
    const sr =
      !searchTerm || searchTerm === ""
        ? null
        : fuseRef.current?.search?.(searchTerm);
    return sr;
  }, [searchTerm, fuseRef.current]);

  const history = useHistory();

  const match = useRouteMatch<{ key: string }>();

  const navigateToKey = useCallback(
    (key) => {
      let path = match?.path
        ?.split?.("/")
        ?.slice?.(0, -1)
        ?.join?.("/");

      Object.entries(match?.params || {}).forEach(([k, v]) => {
        path = path.replace(new RegExp(`:${k}[?]*`), v);
      });
      return history.push(`${path}/${key}`);
    },
    [match, history],
  );

  const mainSettingCategoryAsTabs = useMemo(
    () =>
      mainEntries.current
        // TODO: use isBackoffice instead of hard coding the filter
        ?.filter((e) => showBackofficeSettings || e.key !== "backoffice")
        .filter(
          (entry) =>
            !searchTerm ||
            searchTerm === "" ||
            fuseRef.current
              ?.search?.(searchTerm)
              .some((searchResults) =>
                searchResults.item.key.startsWith(entry.key),
              ),
        )
        .map((e) => ({
          key: e.key,
          name: e.displayName,
          tooltip: e.description,
          component: (
            <KahunSettingEditor
              root={e.key}
              showBackoffice={showBackofficeSettings}
              settingsManager={settingsManager}
              onResetDefaultClick={handleResetDefaultClick}
              filter={searchResults
                ?.filter?.((searchResults) =>
                  searchResults.item.key.startsWith(e.key),
                )
                ?.map?.((fr) => fr.item)}
              highlight={searchTerm}
              onExpandSubCategory={navigateToKey}
              disabledItems={
                props.isMasterClinic ? MASTER_CLINIC_DISABLED_ITEMS : null
              }
              usePath
            />
          ),
        })),
    [mainEntries.current, searchResults, searchTerm, settingsManager],
  );

  const getIndexFromKey = (key) => {
    const index = mainSettingCategoryAsTabs.findIndex(
      (t) => t.key === key?.split?.(".")?.[0],
    );
    if (index < 0) return 0;
    return index;
  };

  const filterByTerm = (newValue: string) => {
    setSearchTerm(newValue);
  };

  if (loading)
    return (
      <KahunConfigurationManagementPageView>
        <LoadingProgress
          label="Loading configuration..."
          style={{ margin: "20px 0" }}
        />
      </KahunConfigurationManagementPageView>
    );

  return (
    <KahunConfigurationManagementPageView
      className={clsx(props.className, "kahun-configuration-management-page")}
    >
      <HeaderControls>
        <FilterSettingsSearchBox
          value={searchTerm}
          onValueChanged={filterByTerm}
        />
        {!settingsManager.isDirty && (
          <>
            <ClearSettingsButton onClick={() => handleResetDefaultClick("")}>
              Clear all settings
              <DeleteForever />
            </ClearSettingsButton>
            <ClearSettingsButton onClick={() => handleImportExportClick("")}>
              Import/Export settings
              <SaveOutlined />
            </ClearSettingsButton>
          </>
        )}
        {settingsManager.isDirty && (
          <>
            <ClearChangesButtons onClick={openResetLastChangesDialog}>
              Clear changes
            </ClearChangesButtons>
            <SaveButton onClick={openSaveDialog}>Save</SaveButton>
          </>
        )}
      </HeaderControls>
      <SettingsContent className={"setting-content"}>
        <KahunSliderTabs
          tabs={mainSettingCategoryAsTabs}
          selectedIndex={getIndexFromKey(match.params.key)}
          onTabSelected={(tab, index) => navigateToKey(tab.key)}
          stickyTabs={true}
        />
      </SettingsContent>
      <SaveSettingsDialog
        open={areYouSureDialog?.isOpen}
        isSaving={areYouSureDialog?.isSaving}
        diff={areYouSureDialog?.diff}
        disabled={disableDialogAction}
        saveStatus={areYouSureDialog?.saveStatus}
        onCancel={closeAreYouSureDialog}
        text={areYouSureDialog?.text}
        saveText={areYouSureDialog?.saveText}
        onSave={handleSave}
      />
      <SaveSettingsDialog
        open={importExport?.isOpen}
        isSaving={importExport?.isSaving}
        diff={importExport?.diff}
        disabled={disableDialogAction}
        saveStatus={importExport?.saveStatus}
        onCancel={closeImportExportDialog}
        text={importExport?.text}
        saveText={importExport?.saveText}
        onSave={handleSave}
        editable
      />
    </KahunConfigurationManagementPageView>
  );
};
